import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Settings, Save, RefreshCw, Euro, Globe, AlertTriangle, Trash2 } from "lucide-react";
import EmailSettingsCard from "../../components/EmailSettingsCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { API } from "../../utils/format";
import { useLang } from "../../context/LangContext";
import { useCompany } from "../../context/CompanyContext";

const SettingsPage = () => {
  const { t } = useLang();
  const { company, refresh } = useCompany();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || "",
        country: company.country || "",
        vat_number: company.vat_number || "",
        address: company.address || "",
        currency: company.currency || "EUR",
        currency_symbol: company.currency_symbol || "€",
        exchange_rate_to_eur: company.exchange_rate_to_eur ?? 1.0,
        display_currency: company.display_currency || "native",
        default_vat_rate: company.default_vat_rate ?? 0.10,
        default_lang: company.default_lang || "it",
        label_company_line1: company.label_company_line1 || "AMPIO GROUP SRL - GIOTTO CARNI",
        label_company_line2: company.label_company_line2 || "Viale Bruno Buozzi 14 - 50053 Empoli (FI)",
        label_company_line3: company.label_company_line3 || "Sed. Operativa: Via Siena 37 int. 28 FIRENZE",
        label_ce_code: company.label_ce_code || "IT M6X1J CE",
      });
    }
  }, [company]);

  if (!company || !form) {
    return <div className="p-8 text-center text-stone-500">{t.loading || "Caricamento..."}</div>;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        exchange_rate_to_eur: parseFloat(form.exchange_rate_to_eur) || 1.0,
        default_vat_rate: parseFloat(form.default_vat_rate) || 0,
      };
      await axios.put(`${API}/companies/${company.id}`, payload);
      toast.success(t.settingsSaved || "Impostazioni salvate");
      refresh();
    } catch (e) {
      toast.error(t.settingsSaveError || "Errore salvataggio: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 flex items-center gap-3">
            <Settings className="w-7 h-7 text-[#8A1C2A]" />
            {t.settings || "Impostazioni"}
          </h1>
          <p className="text-stone-500 text-sm mt-1">{company.name}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="settings-save-btn">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {t.save || "Salva"}
        </Button>
      </div>

      {/* General */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="border-b border-stone-200">
          <CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4" />{t.settingsGeneral || "Dati Aziendali"}</CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t.companyName || "Nome azienda"} value={form.name} onChange={v => setForm({...form, name: v})} testId="settings-name" />
          <Field label={t.country || "Paese (codice)"} value={form.country} onChange={v => setForm({...form, country: v})} testId="settings-country" placeholder="IT, PL, ..." />
          <Field label={t.vatNumber || "Partita IVA / VAT"} value={form.vat_number} onChange={v => setForm({...form, vat_number: v})} testId="settings-vat" />
          <Field label={t.defaultLang || "Lingua predefinita"} value={form.default_lang} onChange={v => setForm({...form, default_lang: v})} testId="settings-lang" placeholder="it, en, pl" />
          <div className="md:col-span-2">
            <Field label={t.address || "Indirizzo"} value={form.address} onChange={v => setForm({...form, address: v})} testId="settings-address" />
          </div>
        </CardContent>
      </Card>

      {/* Etichette Magazzino */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="border-b border-stone-200">
          <CardTitle className="text-base flex items-center gap-2"><Settings className="w-4 h-4 text-[#C5A059]" />Etichette Magazzino (4x6")</CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Intestazione riga 1 (ragione sociale)" value={form.label_company_line1} onChange={v => setForm({...form, label_company_line1: v})} testId="settings-label-line1" />
          <Field label="Bollo CE (es. IT M6X1J CE)" value={form.label_ce_code} onChange={v => setForm({...form, label_ce_code: v})} testId="settings-label-ce" />
          <Field label="Intestazione riga 2 (sede legale)" value={form.label_company_line2} onChange={v => setForm({...form, label_company_line2: v})} testId="settings-label-line2" />
          <Field label="Intestazione riga 3 (sede operativa)" value={form.label_company_line3} onChange={v => setForm({...form, label_company_line3: v})} testId="settings-label-line3" />
        </CardContent>
      </Card>

      {/* Currency & Exchange Rate */}
      <Card className="bg-white border-stone-200">
        <CardHeader className="border-b border-stone-200">
          <CardTitle className="text-base flex items-center gap-2"><Euro className="w-4 h-4 text-[#C5A059]" />{t.settingsCurrency || "Valuta e Cambio"}</CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={t.currencyCode || "Codice valuta"} value={form.currency} onChange={v => setForm({...form, currency: v})} testId="settings-currency" placeholder="EUR, PLN, USD" />
          <Field label={t.currencySymbol || "Simbolo"} value={form.currency_symbol} onChange={v => setForm({...form, currency_symbol: v})} testId="settings-currency-symbol" placeholder="€, zł, $" />
          <div>
            <Label className="text-xs font-semibold text-stone-700">{t.exchangeRate || "Cambio verso EUR"}</Label>
            <Input
              type="number"
              step="0.0001"
              value={form.exchange_rate_to_eur}
              onChange={e => setForm({...form, exchange_rate_to_eur: e.target.value})}
              placeholder="1.00 (EUR base) / 4.30 (PLN/EUR)"
              data-testid="settings-exchange-rate"
            />
            <p className="text-[11px] text-stone-500 mt-1.5">
              {t.exchangeRateHint || "Es: 1 EUR = 4.30 PLN → inserisci 4.30. Imposta 1.0 se la valuta è EUR."}
            </p>
          </div>
          <Field label={t.defaultVatRate || "IVA predefinita (es. 0.10 = 10%)"} value={form.default_vat_rate} onChange={v => setForm({...form, default_vat_rate: v})} testId="settings-vat-rate" placeholder="0.10" type="number" step="0.01" />

          <div className="md:col-span-3">
            <Label className="text-xs font-semibold text-stone-700">{t.displayCurrencyMode || "Modalità calcolo / visualizzazione"}</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <button
                type="button"
                onClick={() => setForm({...form, display_currency: "native"})}
                className={`text-left p-3 border-2 rounded-md transition-all ${form.display_currency !== "eur" ? "border-[#8A1C2A] bg-[#FAF8F5]" : "border-stone-200 hover:border-stone-300"}`}
                data-testid="settings-display-native"
              >
                <p className="font-semibold text-sm">{form.currency_symbol} {t.nativeCurrency || "Valuta nativa"}</p>
                <p className="text-xs text-stone-500 mt-0.5">{t.nativeCurrencyHint || `Tutti i calcoli e i totali sono in ${form.currency_symbol}`}</p>
              </button>
              <button
                type="button"
                onClick={() => setForm({...form, display_currency: "eur"})}
                disabled={form.currency === "EUR"}
                className={`text-left p-3 border-2 rounded-md transition-all ${form.display_currency === "eur" ? "border-[#8A1C2A] bg-[#FAF8F5]" : "border-stone-200 hover:border-stone-300"} ${form.currency === "EUR" ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid="settings-display-eur"
              >
                <p className="font-semibold text-sm">€ {t.eurCurrency || "Euro (convertito)"}</p>
                <p className="text-xs text-stone-500 mt-0.5">{form.currency === "EUR" ? (t.alreadyEur || "Già in EUR") : (t.eurCurrencyHint || `Converti tutto in EUR usando il cambio impostato (÷ ${form.exchange_rate_to_eur})`)}</p>
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live preview */}
      <Card className="bg-gradient-to-br from-[#FAF8F5] to-white border-stone-200">
        <CardHeader className="border-b border-stone-200">
          <CardTitle className="text-base">{t.settingsPreview || "Anteprima"}</CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <PreviewItem label={t.priceExample || "Prezzo esempio"} value={`100.00 ${form.currency_symbol}`} />
          <PreviewItem label={t.convertedToEUR || "Convertito in EUR"} value={`${(100 / (parseFloat(form.exchange_rate_to_eur) || 1)).toFixed(2)} €`} />
          <PreviewItem label={t.exampleVat || "IVA su 100"} value={`${(100 * (parseFloat(form.default_vat_rate) || 0)).toFixed(2)} ${form.currency_symbol}`} />
        </CardContent>
      </Card>

      {/* Email personale SMTP/IMAP */}
      <EmailSettingsCard />

      {/* Danger zone */}
      <DangerZone />
    </div>
  );
};

const DangerZone = () => {
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);

  const openDialog = async () => {
    setConfirmText("");
    setOpen(true);
    setCounts(null);
    try {
      const r = await axios.get(`${API}/admin/danger/wipe-orders/preview`);
      setCounts(r.data);
    } catch (e) {
      toast.error("Errore nel conteggio dei dati");
    }
  };

  const wipe = async () => {
    setWiping(true);
    try {
      const r = await axios.post(`${API}/admin/danger/wipe-orders`, { confirm: confirmText });
      toast.success(`Azzeramento completato: ${r.data.deleted_orders} ordini, ${r.data.deleted_documents} documenti, ${r.data.deleted_chats} chat eliminati`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'azzeramento");
    } finally {
      setWiping(false);
    }
  };

  return (
    <Card className="bg-red-50/50 border-red-200">
      <CardHeader className="border-b border-red-200">
        <CardTitle className="text-base flex items-center gap-2 text-red-800">
          <AlertTriangle className="w-4 h-4" /> Zona pericolosa
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-stone-700 max-w-xl">
          <p className="font-semibold">Azzera ordini e documenti generati dagli ordini</p>
          <p className="text-xs text-stone-500 mt-1">
            Elimina TUTTI gli ordini (di ogni stato), i documenti generati dagli ordini spediti e le relative chat.
            <b> NON tocca</b>: scadenze importate da TeamSystem, clienti, prodotti, listini, offerte, agenti.
            L'operazione è irreversibile.
          </p>
        </div>
        <Button onClick={openDialog} variant="outline" className="border-red-400 text-red-700 hover:bg-red-100" data-testid="danger-wipe-open-btn">
          <Trash2 className="w-4 h-4 mr-2" /> Azzera ordini e documenti
        </Button>
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md bg-white" data-testid="danger-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Conferma azzeramento
            </DialogTitle>
          </DialogHeader>
          {counts ? (
            <div className="text-sm space-y-1 bg-stone-50 border border-stone-200 rounded-md p-3">
              <p>Verranno eliminati:</p>
              <p>• <b data-testid="wipe-count-orders">{counts.orders}</b> ordini</p>
              <p>• <b data-testid="wipe-count-documents">{counts.order_documents}</b> documenti generati dagli ordini</p>
              <p>• <b>{counts.order_chats}</b> chat ordine</p>
              <p className="text-green-700 pt-1">✓ Restano intatte <b>{counts.teamsystem_kept}</b> scadenze importate da TeamSystem</p>
            </div>
          ) : (
            <div className="p-4 text-center"><div className="spinner w-5 h-5 mx-auto" /></div>
          )}
          <div>
            <Label className="text-xs font-semibold text-stone-700">Digita <span className="font-mono text-red-700">AZZERA</span> per confermare</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="AZZERA" className="mt-1 font-mono" data-testid="wipe-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-stone-300">Annulla</Button>
            <Button
              onClick={wipe}
              disabled={confirmText.trim().toUpperCase() !== "AZZERA" || wiping}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="wipe-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {wiping ? "Eliminazione..." : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Field = ({ label, value, onChange, testId, placeholder, type = "text", step }) => (
  <div>
    <Label className="text-xs font-semibold text-stone-700">{label}</Label>
    <Input type={type} step={step} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
  </div>
);

const PreviewItem = ({ label, value }) => (
  <div className="bg-white border border-stone-200 rounded-lg p-4">
    <p className="text-xs text-stone-500 uppercase tracking-wide">{label}</p>
    <p className="text-lg font-bold text-[#8A1C2A] mt-1">{value}</p>
  </div>
);

export default SettingsPage;
